/**
 * Reaction Subscriptions
 *
 * Multiplexes reaction realtime refreshes through one private channel per
 * reaction target type.
 *
 * Primary responsibilities:
 * - Avoid one Realtime subscription per rendered reaction bar
 * - Dispatch target-specific refresh callbacks from shared payloads
 *
 * Dependencies:
 * - Supabase browser client
 * - Browser auth session helper
 */

import type {
  RealtimeChannel,
  SupabaseClient
} from '@supabase/supabase-js'

import { getFreshBrowserSession } from '@/lib/auth/fresh-browser-session'
import { syncPrivateRealtimeAuth } from '@/lib/realtime/private-channel-auth'
import {
  getRealtimeReconnectDelay,
  isRealtimeChannelFailure,
  removeRealtimeChannelOnce,
  shouldRemoveRealtimeChannel
} from '@/lib/realtime/channel-recovery'
import { safeConsole } from '@/lib/utils/safeConsole'
import type { Database } from '@/supabase/types'

export type RealtimeReactionTargetType =
  | 'message'
  | 'space_conversation_message'
  | 'space_task_comment'

type ReactionsClient = Pick<
  SupabaseClient<Database>,
  'auth' | 'channel' | 'realtime' | 'removeChannel'
>

type ReactionPayloadRow = {
  target_id?: unknown
}

type ReactionPayload = {
  new?: ReactionPayloadRow
  old?: ReactionPayloadRow
}

type ReactionChannelManager = {
  channel: RealtimeChannel | null
  handlersByTargetId: Map<string, Set<() => void>>
  isSubscribed: boolean
  key: string
  lastRefreshAt: number
  reconnectAttempt: number
  reconnectTimeout: ReturnType<typeof setTimeout> | null
  refreshTimeout: ReturnType<typeof setTimeout> | null
  supabase: ReactionsClient
  targetType: RealtimeReactionTargetType
  userId: string
}

const managersByKey = new Map<string, ReactionChannelManager>()
const pendingManagersByKey = new Map<
  string,
  Promise<ReactionChannelManager | null>
>()
let lifecycleSubscriberCount = 0

// --------------

function readTargetId(payload: ReactionPayload) {
  const value = payload.new?.target_id ?? payload.old?.target_id

  return typeof value === 'string' && value.trim().length > 0
    ? value
    : null
}

// --------------

function dispatchReactionPayload(
  manager: ReactionChannelManager,
  payload: ReactionPayload
) {
  const targetId = readTargetId(payload)

  if (!targetId) {
    return
  }

  manager.handlersByTargetId.get(targetId)?.forEach(handler => handler())
}

// --------------

function refreshReactionTargetsNow(manager: ReactionChannelManager) {
  manager.lastRefreshAt = Date.now()
  manager.handlersByTargetId.forEach(handlers => {
    handlers.forEach(handler => handler())
  })
}

// Refresh waves must be coalesced: SUBSCRIBED + focus + online +
// visibilitychange can all fire within a second, and an un-coalesced wave
// refetches every rendered reaction bar at once (50+ requests). When those
// requests fail (the usual reason the channel dropped), the wave becomes a
// retry/telemetry storm. At most one wave per window; triggers inside the
// window collapse into a single trailing wave.
const REFRESH_MIN_INTERVAL_MS = 10_000
const REFRESH_TRAILING_DELAY_MS = 1_500

function refreshReactionTargets(manager: ReactionChannelManager) {
  const sinceLast = Date.now() - manager.lastRefreshAt

  if (sinceLast >= REFRESH_MIN_INTERVAL_MS) {
    refreshReactionTargetsNow(manager)
    return
  }

  if (manager.refreshTimeout) {
    return
  }

  manager.refreshTimeout = setTimeout(() => {
    manager.refreshTimeout = null
    if (manager.handlersByTargetId.size > 0) {
      refreshReactionTargetsNow(manager)
    }
  }, REFRESH_TRAILING_DELAY_MS)
}

function clearReactionRefreshTimer(manager: ReactionChannelManager) {
  if (!manager.refreshTimeout) {
    return
  }

  clearTimeout(manager.refreshTimeout)
  manager.refreshTimeout = null
}

// --------------

function clearReactionReconnectTimer(manager: ReactionChannelManager) {
  if (!manager.reconnectTimeout) {
    return
  }

  clearTimeout(manager.reconnectTimeout)
  manager.reconnectTimeout = null
}

// --------------

async function removeReactionChannel(manager: ReactionChannelManager) {
  const channel = manager.channel
  manager.channel = null
  manager.isSubscribed = false

  if (channel) {
    await removeRealtimeChannelOnce(manager.supabase, channel)
  }
}

// --------------

function canKeepReactionRealtimeOpen() {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden'
}

// --------------

function scheduleReactionReconnect(manager: ReactionChannelManager) {
  if (
    manager.reconnectTimeout ||
    manager.handlersByTargetId.size === 0 ||
    !canKeepReactionRealtimeOpen()
  ) {
    return
  }

  const delayMs = getRealtimeReconnectDelay(manager.reconnectAttempt)
  manager.reconnectAttempt += 1
  manager.reconnectTimeout = setTimeout(() => {
    manager.reconnectTimeout = null
    void connectReactionChannelManager(manager)
  }, delayMs)
}

// --------------

async function connectReactionChannelManager(manager: ReactionChannelManager) {
  if (
    manager.channel ||
    manager.handlersByTargetId.size === 0 ||
    !canKeepReactionRealtimeOpen()
  ) {
    return
  }

  try {
    const isReady = await syncPrivateRealtimeAuth(manager.supabase)

    if (!isReady || managersByKey.get(manager.key) !== manager) {
      // Auth/transport is down: do NOT refresh here. Refetching every
      // target while the network is failing is what turned channel drops
      // into fetch/retry floods. The SUBSCRIBED handler refreshes once
      // the channel is actually back.
      scheduleReactionReconnect(manager)
      return
    }

    const channel = manager.supabase.channel(
      `reactions:${manager.targetType}:user:${manager.userId}`,
      {
        config: {
          private: true
        }
      }
    )

    channel.on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'reactions',
        filter: `target_type=eq.${manager.targetType}`
      },
      payload => dispatchReactionPayload(manager, payload as ReactionPayload)
    )

    manager.channel = channel
    channel.subscribe(status => {
      if (managersByKey.get(manager.key) !== manager) {
        return
      }

      if (status === 'SUBSCRIBED') {
        manager.isSubscribed = true
        manager.reconnectAttempt = 0
        clearReactionReconnectTimer(manager)
        refreshReactionTargets(manager)
        return
      }

      if (!isRealtimeChannelFailure(status)) {
        return
      }

      safeConsole.warn('Reaction realtime channel failed', {
        status,
        targetType: manager.targetType
      })
      // No refresh on failure: the channel dropped because transport is
      // unhealthy, so an immediate all-target refetch would fail too and
      // multiply. Recovery refresh happens on the next SUBSCRIBED.

      if (shouldRemoveRealtimeChannel(status)) {
        void removeReactionChannel(manager)
      } else {
        manager.channel = null
        manager.isSubscribed = false
      }

      scheduleReactionReconnect(manager)
    })
  } catch (error) {
    if (managersByKey.get(manager.key) !== manager) {
      return
    }

    safeConsole.warn('Reaction realtime auth sync failed', error)
    await removeReactionChannel(manager)
    scheduleReactionReconnect(manager)
  }
}

// --------------

function releaseHiddenReactionChannels() {
  managersByKey.forEach(manager => {
    clearReactionReconnectTimer(manager)
    clearReactionRefreshTimer(manager)
    void removeReactionChannel(manager)
  })
}

// --------------

function reconnectVisibleReactionChannels() {
  managersByKey.forEach(manager => {
    refreshReactionTargets(manager)
    void connectReactionChannelManager(manager)
  })
}

// --------------

function handleReactionVisibilityChange() {
  if (document.visibilityState === 'hidden') {
    releaseHiddenReactionChannels()
    return
  }

  reconnectVisibleReactionChannels()
}

// --------------

function attachReactionLifecycleListeners() {
  if (typeof window === 'undefined') {
    return
  }

  lifecycleSubscriberCount += 1

  if (lifecycleSubscriberCount > 1) {
    return
  }

  window.addEventListener('focus', reconnectVisibleReactionChannels)
  window.addEventListener('online', reconnectVisibleReactionChannels)
  document.addEventListener('visibilitychange', handleReactionVisibilityChange)
}

// --------------

function detachReactionLifecycleListeners() {
  if (typeof window === 'undefined') {
    return
  }

  lifecycleSubscriberCount = Math.max(0, lifecycleSubscriberCount - 1)

  if (lifecycleSubscriberCount > 0) {
    return
  }

  window.removeEventListener('focus', reconnectVisibleReactionChannels)
  window.removeEventListener('online', reconnectVisibleReactionChannels)
  document.removeEventListener('visibilitychange', handleReactionVisibilityChange)
}

// --------------

async function createReactionChannelManager(
  supabase: ReactionsClient,
  targetType: RealtimeReactionTargetType,
  userId: string,
  key: string
) {
  try {
    const manager: ReactionChannelManager = {
      channel: null,
      handlersByTargetId: new Map(),
      isSubscribed: false,
      key,
      lastRefreshAt: 0,
      reconnectAttempt: 0,
      reconnectTimeout: null,
      refreshTimeout: null,
      supabase,
      targetType,
      userId
    }

    managersByKey.set(key, manager)
    return manager
  } finally {
    pendingManagersByKey.delete(key)
  }
}

// --------------

async function getReactionChannelManager(
  supabase: ReactionsClient,
  targetType: RealtimeReactionTargetType
) {
  const session = await getFreshBrowserSession(supabase)
  const userId = session?.user?.id?.trim()

  if (!userId) {
    return null
  }

  const key = `${targetType}:${userId}`
  const existingManager = managersByKey.get(key)

  if (existingManager) {
    return existingManager
  }

  const pendingManager = pendingManagersByKey.get(key)

  if (pendingManager) {
    return pendingManager
  }

  const nextPendingManager = createReactionChannelManager(
    supabase,
    targetType,
    userId,
    key
  )
  pendingManagersByKey.set(key, nextPendingManager)

  return nextPendingManager
}

// --------------

export async function subscribeToReactionTarget(args: {
  onChange: () => void
  supabase: ReactionsClient
  targetId: string
  targetType: RealtimeReactionTargetType
}) {
  const manager = await getReactionChannelManager(
    args.supabase,
    args.targetType
  )

  if (!manager) {
    return () => {}
  }

  const handlers =
    manager.handlersByTargetId.get(args.targetId) ?? new Set<() => void>()
  handlers.add(args.onChange)
  manager.handlersByTargetId.set(args.targetId, handlers)
  attachReactionLifecycleListeners()

  void connectReactionChannelManager(manager)

  return () => {
    detachReactionLifecycleListeners()
    handlers.delete(args.onChange)

    if (handlers.size === 0) {
      manager.handlersByTargetId.delete(args.targetId)
    }

    if (manager.handlersByTargetId.size > 0) {
      return
    }

    managersByKey.delete(manager.key)
    clearReactionReconnectTimer(manager)
    clearReactionRefreshTimer(manager)
    void removeReactionChannel(manager)
  }
}
